import {
    IPagedSortedSearchResponseModel,
    SortDirections,
} from '../../../../interfaces/PagedSortedSearch';
import {
    ISameCaseMatchedCasesSearchModel,
    SameCaseSortColumns,
} from '../manage/useGetSameCasesQuery';
import { Offcanvas, Stack } from 'react-bootstrap';
import { useEffect, useState } from 'react';

import { CheckmatePagination } from '../../CheckmatePagination';
import { DocumentTitle } from '../../../../components/shared/DocumentTitle';
import { IMatchedCasesSearchResponseModel } from '../../same-cases/view/IMatchedCasesSearchResponseModel';
import { ISaveSameCaseModel } from '../../../../interfaces/ISameCaseModel';
import { Loader } from '../../../../components/shared/Loader';
import { PageWithAffixedHeader } from '../../../../shared/PageWithAffixedHeader';
import { SameCaseAdminSearchResults } from '../../../../components/quad/same-cases-admin/search/same-case-admin-search-results/SameCaseAdminSearchResults';
import { SameCaseAppliedFilters } from '../../../../components/shared/filters/SameCaseAppliedFilters';
import { SameCaseCaseFileModelFilter } from '../../../../components/shared/filters/SameCaseCaseFileModelFilter';
import { SameCaseSearch } from '../../../../components/quad/same-cases-admin/search/SameCaseSearch';
import { SameCasesAdminSearchFilters } from '../../../../components/quad/same-cases-admin/search/SameCasesAdminSearchFilters';
import { cloneDeep } from 'lodash';
import { showErrorToast } from '../../../../shared/ToastUtils';
import { useCaseFileModelContext } from '../../../../contexts/useCaseFileModelContext';
import { useDidUpdate } from '@mantine/hooks';
import { useSameCasesAdminSearchMutation } from './useSameCasesAdminSearchMutation';

const pageTitle = 'Same Case Group Search';
const defaultFilters = {
    pagedModel: {
        pageNumber: 1,
        pageSize: 10,
        rowsPerPage: 10,
    },
    sortModel: {
        sortColumn: SameCaseSortColumns.CaseName,
        sortDirection: SortDirections.Asc,
    },
} as ISameCaseMatchedCasesSearchModel;

export function SameCasesAdminSearchPage() {
    const { caseFileModelId, userCaseFileModelsQuery } = useCaseFileModelContext();
    const searchMutation = useSameCasesAdminSearchMutation();

    const [showFilterPane, setShowFilterPane] = useState<boolean>(false);
    const [currentFilters, setCurrentFilters] =
        useState<ISameCaseMatchedCasesSearchModel>(defaultFilters);
    const [results, setResults] =
        useState<
            IPagedSortedSearchResponseModel<IMatchedCasesSearchResponseModel, SameCaseSortColumns>
        >();
    const [selectedCaseGuids, setSelectedCaseGuids] = useState<string[]>([]);

    const [showAddToCaseGroupModal, setShowAddToCaseGroupModal] = useState<boolean>(false);

    const handleSortData = (key: string, order: string) => {
        const sortColumn = parseInt(key) as SameCaseSortColumns;
        const sortDirection =
            order.toUpperCase() === 'DESC' ? SortDirections.Desc : SortDirections.Asc;

        if (
            sortColumn === currentFilters.sortModel.sortColumn &&
            sortDirection === currentFilters.sortModel.sortDirection
        ) {
            return;
        }

        setCurrentFilters((prev) => ({
            ...prev,
            pagedModel: { ...prev.pagedModel, pageNumber: 1 },
            sortModel: {
                sortColumn,
                sortDirection,
            },
        }));
        setSelectedCaseGuids([]);
    };

    const handleApplyFilters = (filters: ISameCaseMatchedCasesSearchModel) => {
        const filtersCopy = cloneDeep(filters);
        filtersCopy.pagedModel.pageNumber = 1;

        searchMutation
            .mutateAsync(filtersCopy)
            .then((searchResults) => {
                setCurrentFilters(filtersCopy);
                setResults(
                    searchResults as IPagedSortedSearchResponseModel<
                        IMatchedCasesSearchResponseModel,
                        SameCaseSortColumns
                    >
                );
            })
            .catch((error) => {
                showErrorToast(error.message ?? 'Failed to search Same Case Groups');
            });

        setSelectedCaseGuids([]);
    };

    const handleToggleFilterPane = () => {
        setShowFilterPane((prev) => !prev);
    };

    const handleAddToGroupClick = () => {
        setShowAddToCaseGroupModal(true);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentFilters((prev) => ({ ...prev, pagedModel: { ...prev.pagedModel, pageNumber } }));
        setSelectedCaseGuids([]);
    };

    const handlePageSizeChange = (pageSize: number) => {
        setCurrentFilters((prev) => ({
            ...prev,
            pagedModel: { ...prev.pagedModel, pageSize, pageNumber: 1 },
        }));
        setSelectedCaseGuids([]);
    };

    const handleSaveCaseGroup = (caseGroup: ISaveSameCaseModel | undefined) => {
        if (!caseGroup) {
            return;
        }

        const resultsCopy = cloneDeep(results?.results);
        const selectedCases = resultsCopy?.filter((currentCase) =>
            selectedCaseGuids.includes(currentCase.caseGuid)
        );

        selectedCases?.forEach((currentCase) => {
            currentCase.sameCaseGuid = caseGroup.guid;
        });

        setResults({
            ...results,
            results: resultsCopy ?? [],
        } as IPagedSortedSearchResponseModel<
            IMatchedCasesSearchResponseModel,
            SameCaseSortColumns
        >);
        setSelectedCaseGuids([]);
        setShowAddToCaseGroupModal(false);
    };

    useDidUpdate(() => {
        searchMutation.mutateAsync(currentFilters).then((searchResults) => {
            setResults(
                searchResults as IPagedSortedSearchResponseModel<
                    IMatchedCasesSearchResponseModel,
                    SameCaseSortColumns
                >
            );
        });
    }, [
        currentFilters.pagedModel.pageSize,
        currentFilters.pagedModel.pageNumber,
        currentFilters.sortModel.sortColumn,
        currentFilters.sortModel.sortDirection,
    ]);

    useEffect(() => {
        setShowFilterPane(true);
    }, []);

    useEffect(() => {
        if (!searchMutation.isLoading && searchMutation.isSuccess) {
            setShowFilterPane(false);
        } else if (!searchMutation.isLoading && searchMutation.isError) {
            showErrorToast((searchMutation.error as Error).message);
        }
    }, [
        searchMutation.isLoading,
        searchMutation.isSuccess,
        searchMutation.isError,
        searchMutation.error,
    ]);

    useEffect(() => {
        setCurrentFilters({ ...defaultFilters, caseFileModelId });
        setResults(undefined);
        setShowFilterPane(true);
    }, [caseFileModelId]);

    const selectedCases = results?.results.filter((item) =>
        selectedCaseGuids.includes(item.caseGuid)
    );

    return (
        <>
            <DocumentTitle title={`Quad Admin - ${pageTitle}`} hideZoneName />
            <Offcanvas
                show={showFilterPane}
                onHide={() => setShowFilterPane(false)}
                placement="end"
                scroll
                backdrop
            >
                <Offcanvas.Header
                    closeButton
                    style={{ borderBottom: '1px solid lightgray', height: '77px' }}
                >
                    <Offcanvas.Title>
                        <span style={{ fontSize: 20, fontWeight: 500 }}>{pageTitle} Filters</span>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <SameCasesAdminSearchFilters
                        currentFilters={currentFilters}
                        onApplyFilters={handleApplyFilters}
                    />
                </Offcanvas.Body>
            </Offcanvas>

            <SameCaseCaseFileModelFilter
                currentFilters={currentFilters}
                setCurrentFilters={setCurrentFilters}
            />

            <PageWithAffixedHeader
                headerComponent={
                    <>
                        <h1 style={{ margin: 0 }} className="mb-2">
                            {pageTitle}
                        </h1>

                        <Stack direction="horizontal" gap={3} style={{ justifyContent: 'space-between' }}>
                            <SameCaseAppliedFilters
                                currentFilters={currentFilters}
                                onApplyFilters={handleApplyFilters}
                                onToggleFilterPane={handleToggleFilterPane}
                            />
                            <Stack direction="horizontal" style={{ justifyContent: 'flex-end' }}>
                                <button
                                    className="btn-green btn"
                                    disabled={selectedCaseGuids.length < 1}
                                    onClick={handleAddToGroupClick}
                                >
                                    <Stack direction="horizontal" gap={1}>
                                        <i className="fal fa-plus color-white" />
                                        <span className="nowrap">
                                            Add To Same Case Group{' '}
                                            {selectedCaseGuids.length > 0 && (
                                                <>({selectedCaseGuids.length})</>
                                            )}
                                        </span>
                                    </Stack>
                                </button>
                            </Stack>
                        </Stack>
                    </>
                }
            >
                {(searchMutation.isLoading || userCaseFileModelsQuery?.isFetching) && <Loader />}

                <SameCaseAdminSearchResults
                    currentFilters={currentFilters as ISameCaseMatchedCasesSearchModel}
                    isFilterPaneOpen={showFilterPane}
                    isLoading={searchMutation.isLoading}
                    onSelectedCaseGuidsChange={setSelectedCaseGuids}
                    onSort={handleSortData}
                    onToggleFilterPane={() => setShowFilterPane((prev) => !prev)}
                    selectedCaseGuids={selectedCaseGuids}
                    results={results?.results ?? []}
                />

                <CheckmatePagination
                    pagedModel={results?.pagedModel}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </PageWithAffixedHeader>

            {showAddToCaseGroupModal && (
                <SameCaseSearch
                    onHide={() => setShowAddToCaseGroupModal(false)}
                    onSaveCaseGroupComplete={handleSaveCaseGroup}
                    caseNumberSearchValue={selectedCases?.at(0)?.caseNumber}
                    jurisdictionSearchValue={selectedCases?.at(0)?.jurisdictionGuid}
                    caseFileModeSearchValue={caseFileModelId}
                    selectedCases={
                        results?.results.filter((currentCase) =>
                            selectedCaseGuids.includes(currentCase.caseGuid)
                        ) ?? []
                    }
                />
            )}
        </>
    );
}
